import { createImageVariantConfig } from './sdkLoader';

/**
 * 🔴 Story 12.15 (AC-2b/c) — THE ASPECT-PRESERVING VARIANTS THE PUBLIC CARD
 * SURFACES REQUEST. Defined ONCE, here, and imported by every duck that feeds a
 * card — never copy-pasted into seven query blocks.
 *
 * `fit: 'scale'` is a valid custom-variant fit (probed live against the
 * Marketplace API: `imageVariant.frame=w:600;h:600;fit:scale` → 200 OK, `399x600`,
 * resolving to `fit=clip` on the imgix URL; `fit:clip` itself → 400
 * `validation-invalid-value`). It yields a bounding-box file that KEEPS THE
 * PHOTO'S OWN ASPECT, which is the only kind of file a focal point can be
 * honoured against.
 *
 * 🔴 WHY CUSTOM VARIANTS RATHER THAN THE BUILT-IN `scaled-*` FAMILY ON PUBLIC
 * SURFACES. The built-ins are already requested by Search / Home / featured and
 * then discarded by the card's variant filter, so "just use them" looks free.
 * It is not: `scaled-small` is only 320 px on its long edge, below a ~360 px
 * card slot, so the browser jumps to the 750 px `scaled-medium` — a measured
 * +1,290 KB per 24-card page. Right-sizing the file to the box is what makes
 * the public grid affordable.
 *
 * 🔴 THE WIZARD IS DELIBERATELY NOT WIRED FOR THESE, and that is a decision, not
 * an omission. `EditListingPage.duck.js` already requests `scaled-large` +
 * `scaled-xlarge` for every image at its single choke point, and
 * `CARD_FRAMING_VARIANTS` frames from those. The wizard is a one-card-at-a-time
 * surface with no 24-card bandwidth problem, `scaled-large` is what the framer
 * and the tiles already frame against, and a fourth custom variant on the upload
 * path is pure cost. See `util/cardFraming.js`.
 *
 * ⚠️ `frame-1x` at 420 px is a shipped value with a measurement behind it (see
 * the story's AC-16 record), not a guess left at a default.
 */
export const FRAME_VARIANT_DIMENSIONS = {
  'frame-1x': 420,
  'frame-2x': 800,
};

/** `fields.image` entries. Spread into a duck's existing list. */
export const FRAME_IMAGE_FIELDS = ['variants.frame-1x', 'variants.frame-2x'];

/**
 * The `imageVariant.frame-*` query params. Spread into a duck's query object
 * alongside the existing `createImageVariantConfig` calls.
 *
 * A square bounding box (`aspectRatio` 1 ⇒ `h === w`) so the SAME variant serves
 * a landscape, a square and a portrait cover: `fit:'scale'` fits the photo
 * INSIDE the box, so the long edge lands on the requested size whichever way up
 * the photo is.
 */
export const frameImageVariantConfig = () => ({
  ...createImageVariantConfig('frame-1x', FRAME_VARIANT_DIMENSIONS['frame-1x'], 1, 'scale'),
  ...createImageVariantConfig('frame-2x', FRAME_VARIANT_DIMENSIONS['frame-2x'], 1, 'scale'),
});
